/**
 * @file ProductKindField.tsx
 * @description Поле «Вид товара» в карточке товара (phase19.22, W22, макет §6).
 *
 * Первый вариант — «Как в категории — Пищевой» (пустое значение: товар
 * наследует вид категории), дальше — активные виды справочника. Подсказка
 * под полем говорит, откуда вид и что он задаёт; категория без вида —
 * предупреждение (у нового товара — ошибка: сервер ответит 422
 * `product_kind_required`).
 */

import { Label } from "../../ui/label";
import { SelectField } from "../../ui/SelectField";
import type { ProductKind } from "../../../lib/api-product-kinds";
import {
  inheritOptionLabel,
  kindSelectOptions,
  type ResolvedKind,
} from "../../../lib/product-kind-resolve";

/** Текст ошибки «вид обязателен» — тот же смысл, что у 422 `product_kind_required`. */
export const PRODUCT_KIND_REQUIRED_TEXT =
  "У категории вид не задан — выберите вид товара (пищевой, косметика, непищевой…) или задайте его категории.";

interface Props {
  /** Свой вид товара; "" — как в категории. */
  value: string;
  onChange: (kindId: string) => void;
  kinds: ProductKind[];
  /** Вид, который даст категория (для подписи первого варианта). */
  categoryKind: ResolvedKind;
  /** Новый товар: без вида сохранить нельзя. */
  required: boolean;
  disabled?: boolean;
}

export function ProductKindField({
  value,
  onChange,
  kinds,
  categoryKind,
  required,
  disabled,
}: Props) {
  const options = [
    { value: "", label: inheritOptionLabel("Как в категории", categoryKind.kind?.name ?? null) },
    ...kindSelectOptions(kinds, value),
  ];
  const missing = !value && !categoryKind.kind;

  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor="p-product-kind">Вид товара{required ? " *" : ""}</Label>
      <SelectField
        id="p-product-kind"
        ariaLabel="Вид товара"
        value={value}
        onChange={onChange}
        options={options}
        disabled={disabled}
      />
      {missing ? (
        <p className={required ? "text-xs text-destructive" : "text-xs text-warning"}>
          {PRODUCT_KIND_REQUIRED_TEXT}
        </p>
      ) : (
        <p className="text-xs text-on-surface-variant">
          {value
            ? "Свой вид товара, не как у категории."
            : `Из категории «${categoryKind.sourceCategory?.displayName ?? "—"}».`}{" "}
          Срок, этикетка и НДС берутся из вида.
        </p>
      )}
    </div>
  );
}
